// MODBUS_APP_HOME1/src/App.js
import React, { useState, useEffect } from 'react';
import './App.css'; // Import the CSS for styling
import { jwtDecode } from "jwt-decode"; // Import the library to decode JWTs
import AccelLogo from './ACCEL_LOGO.png';
import TeLogo from './TE_LOGO.png';
import AxiomLogo from './AXIOM_LOGO.png';

// --- CHILD COMPONENTS ---

const AlarmStatusBox = ({ plcData, alarmConfig }) => {
    const [alarmStatusMessage, setAlarmStatusMessage] = useState('Checking...');

    useEffect(() => {
        if (plcData && plcData.holdingRegisters && alarmConfig && alarmConfig.length > 0) {
            const alarmCode = plcData.holdingRegisters[151];
            if (alarmCode && alarmCode > 0) {
                const alarm = alarmConfig.find(a => a.type === 'stateRegister' && a.address === 151 && a.triggerValue === alarmCode);
                
                if (alarm && alarm.triggerValue === 23) {
                    setAlarmStatusMessage(`✅ ${alarm.name}`);
                } else {
                    setAlarmStatusMessage(alarm ? `🚨 ${alarm.name}` : `Unknown Alarm: ${alarmCode}`);
                }
            } else {
                setAlarmStatusMessage('✅ System OK');
            }
        } else if (!plcData) {
            setAlarmStatusMessage('Status Unavailable');
        } else {
            setAlarmStatusMessage('✅ System OK');
        }
    }, [plcData, alarmConfig]);

    const boxClass = alarmStatusMessage.startsWith('🚨') 
        ? 'alarm-status-box-home alarm' 
        : 'alarm-status-box-home ok';

    return (
        <div className={boxClass}>
            <span>{alarmStatusMessage}</span>
        </div>
    );
};

const CompressorStatusBox = ({ plcData }) => {
    const [compressorStatus, setCompressorStatus] = useState('Checking...');

    useEffect(() => {
        if (plcData && plcData.holdingRegisters) {
            const statusValue = plcData.holdingRegisters[16];
            switch (statusValue) {
                case 0: setCompressorStatus('Idle'); break;
                case 1: setCompressorStatus('Starting Soon'); break;
                case 2: setCompressorStatus('Running'); break;
                case 3: setCompressorStatus('Faulted'); break;
                case 4: setCompressorStatus('Running w/Alarm'); break;
                default: setCompressorStatus('Unknown'); break;
            }
        } else {
            setCompressorStatus('Unavailable');
        }
    }, [plcData]);

    const boxClass = `compressor-status-box ${compressorStatus.toLowerCase().replace(/\s+/g, '-')}`;

    return (
        <div className={boxClass}>
            <span>Compressor: {compressorStatus}</span>
        </div>
    );
};


const StatusIndicator = ({ label, status }) => {
    let color = 'bg-gray-500';
    let text = 'Checking...';
    if (status === 'online') { color = 'bg-green-500'; text = 'Online'; }
    else if (status === 'offline') { color = 'bg-red-500'; text = 'Offline'; }
    else if (status === 'error') { color = 'bg-yellow-500'; text = 'Error'; }
    return (
        <div style={{ display: 'flex', alignItems: 'center', minWidth: '120px' }}>
            <span style={{ fontSize: '14px', fontWeight: '500', marginRight: '8px', width: '50px' }}>{label}:</span>
            <div style={{ width: '12px', height: '12px', borderRadius: '50%', marginRight: '8px' }} className={color}></div>
            <span style={{ fontSize: '14px', fontWeight: '500' }}>{text}</span>
        </div>
    );
};

const DashboardItem = ({ name, url, dashboardData, navigateToDashboard }) => {
    const FIVE_MINUTES = 5 * 60 * 1000;
    const FIFTEEN_MINUTES = 15 * 60 * 1000;

    let commsDisplayStatus = 'checking';
    if (dashboardData) {
        if (dashboardData.serverStatus === 'online') {
            commsDisplayStatus = 'online';
        } else {
            const timeSinceFailure = Date.now() - (dashboardData.serverFirstFailureTimestamp || Date.now());
            commsDisplayStatus = timeSinceFailure < FIVE_MINUTES ? 'error' : 'offline';
        }
    }

    let plcDisplayStatus = 'checking';
    if (dashboardData) {
         if (dashboardData.plcStatus === 'online') {
            plcDisplayStatus = 'online';
        } else {
            const timeSinceFailure = Date.now() - (dashboardData.plcFirstFailureTimestamp || Date.now());
            if (timeSinceFailure < FIVE_MINUTES) plcDisplayStatus = 'checking';
            else if (timeSinceFailure < FIFTEEN_MINUTES) plcDisplayStatus = 'error';
            else plcDisplayStatus = 'offline';
        }
    }

    return (
        <div className="dashboard-item">
            <button className={`${name.replace(/\s+/g, '_')}-button`} onClick={() => navigateToDashboard(url)}>
                {name} Dashboard
            </button>
            <div className="status-container">
                <StatusIndicator label="Comms" status={commsDisplayStatus} />
                <StatusIndicator label="PLC" status={plcDisplayStatus} />
            </div>
            <div className="status-boxes-container">
                <AlarmStatusBox plcData={dashboardData?.plcData} alarmConfig={dashboardData?.alarmConfig} />
                <CompressorStatusBox plcData={dashboardData?.plcData} />
            </div>
        </div>
    );
};


// --- MAIN APP COMPONENT ---
function App() {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [loginError, setLoginError] = useState('');
    const [userRoles, setUserRoles] = useState([]);
    const [dashboardData, setDashboardData] = useState({});

    const API_GATEWAY_URL = 'https://api.thomasevolutioncontrols.com';
    const dashboardBackends = React.useMemo(() => ({

        'SANDHILLS BOOSTER UNIT 1 (211)':{
            url: 'https://plc-211.thomasevolutioncontrols.com',
            id: '211'
        },

        'SANDHILLS BOOSTER UNIT 3 (212)':{
            url: 'https://plc-212.thomasevolutioncontrols.com',
            id: '212'
        },

        'SHTU 425 (213)':{
            url: 'https://plc-213.thomasevolutioncontrols.com',
            id: '213'
        },

        'SATELLITE 16 (214)':{
            url: 'https://plc-214.thomasevolutioncontrols.com',
            id: '214'
        },

        'SHTU 4508 (215)':{
            url: 'https://plc-215.thomasevolutioncontrols.com',
            id: '215'
        },

        'SANDHILLS BOOSTER UNIT 2 (216)': {
            url: 'https://plc-216.thomasevolutioncontrols.com',
            id: '216'
        },

        'v570-plc)': {
            url: 'https://v570-plc.thomasevolutioncontrols.com',
            id: '101'
        },
        
    }), []);

    useEffect(() => {
        if (!isLoggedIn) return;

        const fetchAllData = () => {
            const token = localStorage.getItem('jwt_token');
            if (!token) return;
            const headers = { 'Authorization': `Bearer ${token}` };

            for (const [name, config] of Object.entries(dashboardBackends)) {
                
                Promise.all([
                    fetch(`${API_GATEWAY_URL}/api/status/${config.id}`, { headers }),
                    fetch(`${API_GATEWAY_URL}/api/scada-data/${config.id}`, { headers }),
                    fetch(`${API_GATEWAY_URL}/api/alarms/config/${config.id}`, { headers })
                ]).then(async ([statusRes, dataRes, configRes]) => {
                    
                    if (statusRes.ok && dataRes.ok && configRes.ok) {
                        const statusData = await statusRes.json();
                        const plcData = await dataRes.json();
                        const alarmConfig = await configRes.json();

                        setDashboardData(prev => {
                            const entry = prev[name] || {};
                            return {
                                ...prev,
                                [name]: {
                                    ...entry,
                                    serverStatus: 'online',
                                    plcStatus: statusData.plc,
                                    plcData: plcData,
                                    alarmConfig: alarmConfig,
                                    serverFirstFailureTimestamp: null,
                                    plcFirstFailureTimestamp: statusData.plc === 'online' ? null : (entry.plcFirstFailureTimestamp || Date.now())
                                }
                            };
                        });
                    } else {
                        throw new Error(`Failed to fetch data for ${name}`);
                    }
                }).catch((error) => {
                    console.error(error);
                    setDashboardData(prev => {
                        const entry = prev[name] || {};
                        return {
                            ...prev,
                            [name]: {
                                ...entry,
                                serverStatus: 'offline',
                                plcStatus: 'unknown',
                                plcData: null,
                                alarmConfig: [],
                                serverFirstFailureTimestamp: entry.serverFirstFailureTimestamp || Date.now(),
                                plcFirstFailureTimestamp: entry.plcFirstFailureTimestamp || Date.now()
                            }
                        };
                    });
                });
            }
        };

        fetchAllData();
        const intervalId = setInterval(fetchAllData, 10000);
        return () => clearInterval(intervalId);
    }, [isLoggedIn, dashboardBackends]);

    useEffect(() => {
        const token = localStorage.getItem('jwt_token');
        if (token) {
            try {
                const decodedToken = jwtDecode(token);
                if (decodedToken.exp * 1000 < Date.now()) {
                    localStorage.removeItem('jwt_token');
                    console.error("Token expired.");
                } else {
                    setUserRoles(decodedToken.roles || []);
                    setIsLoggedIn(true);
                }
            } catch (e) {
                console.error("Invalid token on load:", e);
                localStorage.removeItem('jwt_token');
            }
        }
    }, []);

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoginError('');
        try {
            const response = await fetch(`${API_GATEWAY_URL}/api/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, password }),
            });
            const data = await response.json();
            if (response.ok && data.token) { // Check for token directly
                localStorage.setItem('jwt_token', data.token);
                const decodedToken = jwtDecode(data.token);
                setUserRoles(decodedToken.roles || []);
                setIsLoggedIn(true);
            } else {
                throw new Error(data.message || 'Login failed.');
            }
        } catch (error) {
            setLoginError(error.message);
            localStorage.removeItem('jwt_token');
        }
    };

    const handleLogout = async () => {
        const token = localStorage.getItem('jwt_token');
        if (token) {
            try {
                await fetch(`${API_GATEWAY_URL}/api/auth/logout`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` }
                });
            } catch (error) {
                console.error('Server-side logout failed, continuing client-side.', error);
            }
        }
        setIsLoggedIn(false);
        setUsername('');
        setPassword('');
        setLoginError('');
        setUserRoles([]);
        localStorage.removeItem('jwt_token');
    };

    const navigateToDashboard = (url) => {
        const token = localStorage.getItem('jwt_token');
        window.location.href = token ? `${url}?token=${token}` : url;
    };
    
    // --- START OF CHANGE ---
    // Added a specific style for the Accel logo to adjust its size.
    const AppStyles = `
        .header-logo {
            max-width: 150px;
            height: auto;
        }
        .accel-logo-style {
            max-width: 180px; /* Making this logo slightly larger */
            height: auto;
        }
        .header-top-row {
            display: flex;
            justify-content: space-around;
            align-items: center;
            width: 100%;
        }
        .login-header {
            width: 100%;
            max-width: 1200px;
            padding: 20px;
            box-sizing: border-box;
        }
        .login-container {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            min-height: 100vh;
            padding: 20px;
            box-sizing: border-box;
        }
        
        @media (max-width: 768px) {
            .header-top-row {
                flex-direction: column;
                gap: 2rem;
            }
        }
    `;
    // --- END OF CHANGE ---

    if (!isLoggedIn) {
        return (
            <>
                <style>{AppStyles}</style>
                <div className="login-container">
                    <div className="login-header">
                        <div className="header-top-row">
                            <img src={AxiomLogo} alt="Axiom Link Logo" className="header-logo" />
                            <img src={TeLogo} alt="Thomas Evolution LLC Logo" className="header-logo" />
                            {/* --- START OF CHANGE --- */}
                            {/* Applied the new, larger style to the Accel logo */}
                            <img src={AccelLogo} alt="Accel Compression Inc. Logo" className="accel-logo-style" />
                            {/* --- END OF CHANGE --- */}
                        </div>
                    </div>
                    <div className="login-box">
                        <h1 className="login-title">User Login</h1>
                        <p className="login-description">Access the Axiom Link Portal by Thomas Evolution</p>
                        <form onSubmit={handleLogin} className="login-form">
                            <div className="input-group">
                                <label htmlFor="username">Username:</label>
                                <input type="text" id="username" value={username} onChange={(e) => setUsername(e.target.value)} required className="login-input" />
                            </div>
                            <div className="input-group">
                                <label htmlFor="password">Password:</label>
                                <input type="password" id="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="login-input" />
                            </div>
                            {loginError && <p className="error-message">{loginError}</p>}
                            <button type="submit" className="login-button">Login</button>
                        </form>
                    </div>
                </div>
            </>
        );
    }

    const canViewDashboards = userRoles.includes('admin') || userRoles.includes('operator');

    return (
        <div className="home-container">
            <style>{AppStyles}</style>
            <header className="home-header">
                <div className="header-top-row">
                    <img src={AxiomLogo} alt="Axiom Link Logo" className="header-logo" />
                    <img src={TeLogo} alt="Thomas Evolution LLC Logo" className="header-logo" />
                    {/* --- START OF CHANGE --- */}
                    {/* Applied the new, larger style to the Accel logo */}
                    <img src={AccelLogo} alt="Accel Compression Inc. Logo" className="accel-logo-style" />
                    {/* --- END OF CHANGE --- */}
                </div>
                <div className="flex justify-between items-center w-full mb-4">
                    <h1 className="home-title-small">Accel Compression Main Portal</h1>
                    <button className="logout-button" onClick={handleLogout}>
                        Logout
                    </button>
                </div>
                <p className="home-description">
                    Welcome! 
                </p>
                {canViewDashboards && Object.entries(dashboardBackends).map(([name, config]) => (
                    <DashboardItem
                        key={name}
                        name={name}
                        url={config.url}
                        dashboardData={dashboardData[name]}
                        navigateToDashboard={navigateToDashboard}
                    />
                ))}
            </header>
            <footer className="home-footer">
                <div className="info-boxes-container">
                    <div className="info-box">
                        <h3>Thomas Evolution LLC</h3>
                        <ul>
                            <li><strong>Name:</strong> <span>Zachary T Scott</span></li>
                            <li><strong>Cell:</strong> <span>(210) 482-0659</span></li>
                            <li><strong>Email:</strong> <span>ThomasEvolution13@gmail.com</span></li>
                            <li><strong>Address:</strong> <span>123</span></li>
                            <li><strong>Comment:</strong> <span>Your custom comment here.</span></li>
                            <li><strong>Website:</strong> <span>https</span></li>
                        </ul>
                    </div>
                    <div className="info-box">
                        <h3>Accel Compression, inc.</h3>
                        <ul>
                            <li><strong>Name:</strong> <span>Roger Becker</span></li>
                            <li><strong>Cell:</strong> <span>(432) 563-1376</span></li>
                            <li><strong>Email:</strong> <span>Roger@accelcompression.com</span></li>
                            <li><strong>Address:</strong> <span>4500 S County Rd 1310, Odessa, TX 79765</span></li>
                            <li><strong>Comment:</strong> <span>Accelerating America’s Natural Gas</span></li>
                            <li><strong>Website:</strong> <span>https://accelcompression.com</span></li>
                        </ul>
                    </div>
                </div>
            </footer>
        </div>
    );
}

export default App;

